import React from "react";
import { Link } from "react-router-dom";
import { Award, Heart, Microscope, Sparkles } from "lucide-react";
import { IMAGES, BRAND } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, personSchema, organizationSchema, breadcrumbSchema } from "../data/seo";

const FEATURES = [
  {
    icon: Award,
    title: "Dentistry should never feel routine.",
    body: "Every smile tells a different story. That is why every treatment begins with listening, understanding your goals, and designing a plan that protects your health while creating natural, lasting results.",
  },
  {
    icon: Heart,
    title: "Preserve before replacing.",
    body: "The strongest tooth is the one you were born with. Whenever possible, treatment is designed to conserve healthy tooth structure and restore function with the least invasive approach.",
  },
  {
    icon: Microscope,
    title: "Beautiful should never look artificial.",
    body: "Exceptional cosmetic dentistry is measured by what people do not notice. Every smile is designed to complement your facial features, personality, and natural expressions.",
  },
  {
    icon: Sparkles,
    title: "Every detail matters.",
    body: "Advanced technology, meticulous planning, and decades of clinical experience come together to deliver dentistry that is functional, durable, and exceptionally refined.",
  },
];

export const AboutPage = () => (
  <div data-testid="about-page">
    <Seo
      title={SEO.about.title}
      description={SEO.about.description}
      image={IMAGES.Drk}
      type="profile"
      schemas={[
        personSchema(),
        organizationSchema(),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
      ]}
    />
    {/* Hero */}
    <section className="relative h-[60vh] overflow-hidden">
      <img src={IMAGES.Drk} alt="Dr. K Portrait" className="absolute  inset-0 w-full h-full object-cover object-center "  
      style={{ objectPosition: "50% 25%" }}
      />
      {/* <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/80" /> */}
      <div className="relative h-full container-wide flex flex-col justify-end pb-12 md:pb-24 text-[#ff6a00]">
        {/* <div className="label-eyebrow text-white mb-6">About</div> */}
        <h1 className="heading-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl max-w-4xl lg:max-w-none lg:whitespace-nowrap text-[#ff6a00] drop-shadow-[0_2px_14px_rgba(255,255,255,0.7)]">
          Behind every smile <span className="italic text-[#fff200]">there is a story.</span>
        </h1>
        <p className="font-serif font-semibold italic text-lg sm:text-xl md:text-2xl text-[#ff8a00] mt-4 md:mt-6 max-w-2xl">
        About Dr.K - Behind every dentist, there is a purpose. 
        </p>
      </div>
    </section>

    {/* Dr. K Difference */}
    <section className="py-10 container-x">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
          <div className="label-eyebrow mb-6">The Difference</div>
          <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl">
            Dentistry, but practiced as <span className="italic text-gold">fine art.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-8 text-ink/80 text-lg leading-[1.8] reveal">
          <p>
            I became a dentist because I wanted to give people something that nothing else could — a reason to smile freely. Not a Hollywood smile. <em>Their</em> smile. The one they were meant to have.
          </p>
          <p>
            For nearly twenty years, I've practiced with one guiding belief: my office should feel like an extension of your home. Welcoming. Comfortable. A place where you come to relax, heal, rejuvenate — and walk out feeling great about yourself and life in general.
          </p>
          <p>
            We provide the best and latest in technology and treatment options, but technology is only as good as the hands that use it. Every restoration is hand-finished. Every plan is built around <em>you</em> — your concerns, your goals, your story. Your voice is always heard.
          </p>
          <p>
            From biomimetic and holistic dentistry to full smile makeovers, dental implants, and our exclusive in-office wellness spa — we're here to care for the whole you. Not just the teeth.
          </p>
          <p className="font-serif italic text-2xl text-gold-dark border-l-2 border-gold pl-6 my-12">
            "A beautiful smile is the architecture of confidence. My job is to build it well."
          </p>
          <p className="text-sm tracking-widest uppercase text-ink/50">— {BRAND.doctor}</p>
        </div>
      </div>
    </section>

    {/* Why Dr. K Feature Grid */}
    <section className="py-24 md:py-32 bg-ink text-bone">
      <div className="container-x">
        <SectionHeading eyebrow="Why Dr. K" title="What you can" italicWord="expect." light />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-ink p-10 md:p-14 reveal" data-testid={`feature-${i}`} style={{ transitionDelay: `${i * 60}ms` }}>
                {/* <Icon size={28} strokeWidth={1} className="text-gold mb-8" /> */}
                <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight">{f.title}</h3>
                <p className="text-white/60 mt-4 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 container-x text-center">
      <div className="max-w-3xl mx-auto">
        <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl text-ink">
          A Different <span className="italic text-gold">Patient Experience</span>
        </h2>
        <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink/85">
          Care designed around you.
        </p>
        <p className="mt-6 text-ink/75 leading-relaxed">
          From your first consultation to your final result, every visit is intentionally calm, unrushed, and personal. Questions are encouraged. Decisions are collaborative. Your comfort is never an afterthought.
        </p>
      </div>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/contact" data-testid="about-book-cta" className="btn-primary">Book a Consultation</Link>
        {/* <Link to="/services" data-testid="about-services-cta" className="btn-outline">View Services</Link> */}
      </div>
    </section>
  </div>
);
